import { RedDotListData, TmapApp, UserAllSettings } from '@tmap-web-lib/tmap-app-interface'
import { convertWgs84ToSk } from '@tmap-web-lib/utils'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import debounce from 'lodash-es/debounce'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { gte } from 'semver'

import BizBanner from '@/assets/images/img_my_menu_biz.svg'
import { usePageIdContext } from '@/Providers'
import { AccessKeyRefContext } from '@/Providers/InitializationProvider'
import { getAccessKeyWithRetry } from '@/Providers/access-key'
import { useTmapLogContext } from '@/Providers/tmap-log-context'
import { accessKeyAtom, frontmanState, globalState, storageSchemeUrl } from '@/atoms'
import { MainSkeleton } from '@/components'
import { sendEventLog } from '@/features/log/log.fn'
import MyLogDefinition from '@/features/log/log.my'
import {
  CavatarBanner,
  MileageGradeType,
  PlaceReview,
  Point,
  PointMissionBanner,
  ServiceList,
  UserPointProps,
  UserProfile,
  UserProfileProps,
  VehicleInfo,
  VehicleInfoProps,
  FollowingContents,
} from '@/features/web/components'
import { RewardShopping } from '@/features/web/components/reward-shopping'
import { fetchPoiVisitCountRecent } from '@/features/web/move_log/api'
import { useOpenService, useOpenServiceUrl, useQueryWrapper } from '@/hooks'
import { useVideoAutoplay } from '@/hooks/useVideoAutoplay'
import {
  useGetAdsServing,
  useGetBizCouponCount,
  useGetBoardList,
  useGetDrivePassengerGradeEvent,
  useGetHomeQuiz,
  useGetMomentUsableCouponCount,
  useGetMyWholeReview,
  useGetParkingList,
  useGetRewardShoppingAmount,
  useGetServiceConfiguration,
  useGetTmapPayPointsSummary,
  useGetUserInfo,
} from '@/react-query'
import { fuelTypes, vehicleModelTypes } from '@/types'
import { envServiceUrl, tmapDateStringToDateTime } from '@/utils'
import { logError } from '@/utils/rum'

import * as styles from '@/styles/pages/web.css'

const AdsBanner = dynamic(
  () => import('@/features/web/components/ads-banner/AdsBanner'),
  { ssr: false, loading: () => null },
)

dayjs.locale('ko')

export type WebMyMainContentStartFrom = 'userProfile' | 'vehicleInfo'

export interface WebMyMainContentProps {
  startFrom: WebMyMainContentStartFrom
  /**
   * /web 에서는 interfaceArea가 비어있지만 필요할 수 있어 옵션으로 둠.
   * /web/my 에서는 MyProfilePage가 interfaceArea를 가지므로 false로 두는 게 일반적.
   */
  showInterfaceArea?: boolean

  /**
   * WebMyMainContent 내부에서 MainSkeleton을 직접 표출할지 여부.
   * - /web 에서는 true(기본값)로 내부 스켈레톤을 사용
   * - /web/my 처럼 페이지에서 별도의 스켈레톤을 관리하는 경우 false로 전달해 중복 노출을 방지
   */
  showMainSkeleton?: boolean
  onReadyRefreshMyContent?: (refresh: () => Promise<void>) => void
}

export function WebMyMainContent({
  startFrom,
  showInterfaceArea = true,
  showMainSkeleton = true,
  onReadyRefreshMyContent,
}: WebMyMainContentProps) {
  const setGlobalStateValue = useSetAtom(globalState)
  const accessKey = useAtomValue(accessKeyAtom)
  const accessKeyRef = useContext(AccessKeyRefContext)
  const { canAutoplay, resumeKey, refreshOnResume: refreshAutoplayOnResume } = useVideoAutoplay()
  const { hasError } = useAtomValue(frontmanState)
  const [schemeUrlStorageValue, setSchemeUrlStorageValue] = useAtom(storageSchemeUrl)
  const globalStateValue = useAtomValue(globalState)

  const bizPlaceBanner = globalStateValue.MY_BIZ_PLACE_BANNER ?? {
    url: 'tmap://board?pageid=board_detail&boardid=BOARD-MAIN&seq=230',
    isShow: true,
  }
  const cavatarBannerData = globalStateValue.MY_CAVARTAR_BANNER as { AOS: string; iOS: string; text: string } | undefined

  const { action_id } = MyLogDefinition.my
  const { tmapLogClient } = useTmapLogContext()
  const openService = useOpenService()
  const openServiceUrl = useOpenServiceUrl()
  const isErrorLoggedRef = useRef(false)
  const pageIdContext = usePageIdContext()
  const env = process.env.NEXT_PUBLIC_TARGET

  const rewardShoppingFlagKey =
    process.env.NEXT_PUBLIC_TARGET === 'prd' && process.env.NEXT_PUBLIC_BUILD_ENV === 'RTG'
      ? 'MY_REWARD_SHOPPING_MODULE_RTG'
      : 'MY_REWARD_SHOPPING_MODULE'
  const isRewardShoppingModuleVisible = (globalStateValue[rewardShoppingFlagKey] as { isShow: boolean } | undefined)?.isShow ?? false

  const {
    data: serviceConfigData,
    isFetched: isServiceConfigFetched,
    refetch: refetchServiceConfig,
    error: serviceConfigError,
  } = useGetServiceConfiguration({ keyGroup: 'MY', limit: 1000 }, { useErrorBoundary: false })

  const { data: bannerList, isFetched: isBannerFetched } = useGetAdsServing({
    inventoryCode: 'IN104',
    useErrorBoundary: false,
  })

  const {
    data: quizData,
    isFetched: isQuizDataFetched,
    refetch: refetchQuizData,
  } = useGetBoardList({ boardCode: 'BOARD-MY-QUIZ-MISSION', useErrorBoundary: false })

  const missionInventoryCodes = useMemo(() => {
    const raw = globalStateValue.MY_ADS_MISSION_INVENTORY_CODES
    if (!raw) return []
    return raw?.ads?.adMissionInventoryCodes ?? []
  }, [globalStateValue.MY_ADS_MISSION_INVENTORY_CODES])

  const hasCodes = missionInventoryCodes.length > 0
  const {
    data: missionBannerList,
    isFetched: isMissionBannerFetched,
    refetch: refetchMissionBannerList,
  } = useGetAdsServing({
    useErrorBoundary: false,
    enabled: hasCodes,
    inventoryCodes: hasCodes ? missionInventoryCodes.join(',') : '',
  })

  const {
    data: pointData,
    isFetched: isPointDataFetched,
    refetch: refetchPointData,
    error: pointDataError,
  } = useGetTmapPayPointsSummary({ useErrorBoundary: false })

  const {
    data: drivePassengerGradeEventData,
    isFetched: isDrivePassengerGradeEventFetched,
    refetch: refetchDrivePassengerGradeEventData,
    error: drivePassengerGradeEventError,
  } = useGetDrivePassengerGradeEvent({ useErrorBoundary: false })

  const {
    data: couponData,
    isFetched: isCouponDataFetched,
    refetch: refetchCouponData,
    error: couponDataError,
  } = useGetMomentUsableCouponCount({ useErrorBoundary: false })

  const {
    data: bizCouponData,
    isFetched: isBizCouponDataFetched,
    refetch: refetchBizCouponData,
    error: bizCouponDatatError,
  } = useGetBizCouponCount({ useErrorBoundary: false })

  const {
    data: userInfoData,
    isFetched: isUserInfoDataFetched,
    refetch: refetchUserInfoData,
    error: userInfoError,
  } = useGetUserInfo({ useErrorBoundary: false })

  const {
    data: parkingData,
    isFetched: isParkingDataFetched,
    refetch: refetchParkingData,
    error: parkingDataError,
  } = useGetParkingList({ useErrorBoundary: false })

  const {
    data: reviewData,
    isFetched: isReviewDataFetched,
    refetch: refetchReviewData,
    error: reviewDataError,
  } = useGetMyWholeReview({ useErrorBoundary: false })

  const {
    data: homeQuizData,
    isFetched: isHomeQuizFetched,
    refetch: refetchHomeQuiz,
    error: homeQuizError,
  } = useGetHomeQuiz({ useErrorBoundary: false })

  const { data: poiVisitedCount, refetch: refetchPoiVisitedCount } = useQueryWrapper(
    ['fetchPoiVisitCountRecent'],
    async () => {
      const { data } = await fetchPoiVisitCountRecent()
      return data
    },
    { useErrorBoundary: false },
  )

  const isVerified = userInfoData?.idVrfdYn === 'Y'
  const bannerData = bannerList?.ads

  const [isMainLoading, setIsMainLoading] = useState(true)
  const [followingPosition, setFollowingPosition] = useState<{ lon: number; lat: number } | null>(null)
  const refetchFollowingContentsRef = useRef<(() => void) | null>(null)

  const {
    data: rewardShoppingPointData,
    refetch: refetchRewardShoppingPoint,
    isError: isRewardShoppingPointError,
  } = useGetRewardShoppingAmount({ useErrorBoundary: false, enabled: isRewardShoppingModuleVisible })

  const rewardShoppingPoint = rewardShoppingPointData?.availableAmount ?? null

  const rewardShoppingTextConfig = serviceConfigData?.data.contents.find(
    (item) => item.key === 'MY_REWARD_SHOPPING_TEXT',
  )?.data as { text: string; isShow: boolean } | undefined

  useEffect(() => {
    TmapApp.getCurrentPosition().then((pos) => {
      if (pos) {
        const sk = convertWgs84ToSk({ longitude: pos[1], latitude: pos[0] })
        setFollowingPosition({ lon: sk.x, lat: sk.y })
      }
    })
  }, [])

  const [pointRedDot, setPointRedDot] = useState<RedDotListData[]>([])
  const [insuranceRedDot, setInsuranceRedDot] = useState<RedDotListData[]>([])
  const [missionRedDot, setMissionRedDot] = useState<RedDotListData[]>([])
  const [bizCouponRedDot, setBizCouponRedDot] = useState<RedDotListData[]>([])
  const [followingRedDot, setFollowingRedDot] = useState<RedDotListData[]>([])
  const [serviceListRedDot, setServiceListRedDot] = useState<
    Pick<RedDotListData, 'itemGroup' | 'item'>[]
  >([{ itemGroup: '', item: '' }])
  const [messageList, setMessageList] = useState<RedDotListData[]>([])

  const [userProfileData, setUserProfileData] = useState<UserProfileProps>({
    loginAuthType: '',
    image: '',
    name: '',
    mdn: '',
    tid: '',
    isVerified: false,
  })

  const [vehicleInfo, setVehicleInfo] = useState<Omit<VehicleInfoProps, 'redDotList'>>({
    carNumber: null,
    vehicleType: '',
    fuelType: '',
    isParking: null,
  })

  const [userPoint, setUserPoint] = useState<
    Omit<UserPointProps, 'pointRedDotList' | 'bizCouponRedDotList'>
  >({
    point: 0,
    mileage: 0,
    grade: 'BRONZE',
    isEventOngoing: false,
    text: '',
    usableCouponCount: 0,
    usableBizCouponCount: 0,
  })

  const getLoginData = useCallback(async () => {
    const loginAuthType = await TmapApp.getLoginMethod()
    const userInfo = {
      loginAuthType: loginAuthType || 'None',
      tid:
        loginAuthType === 'TID' && userInfoData
          ? userInfoData.externalUserInfos[0].readableUserId
          : '',
    }
    setUserProfileData((prev) => ({ ...prev, ...userInfo }))
  }, [userInfoData])

  useEffect(() => {
    if (window.location.pathname === '/web' || window.location.pathname === '/web/my') {
      setGlobalStateValue((prev) => ({ ...prev, isSkeleton: true, notFoundError: false }))
    }
  }, [setGlobalStateValue])

  useEffect(() => {
    if (pointData) {
      setUserPoint((prev) => ({
        ...prev,
        point: pointData.tmapPoint.availablePoint,
        mileage: pointData.mileage.availableMileage,
      }))
    }
  }, [pointData])

  useEffect(() => {
    if (drivePassengerGradeEventData) {
      setUserPoint((prev) => ({
        ...prev,
        grade: drivePassengerGradeEventData.passengerGrade as MileageGradeType,
        isEventOngoing: drivePassengerGradeEventData.eventPointAccumulationRate > 0,
        text:
          drivePassengerGradeEventData.eventPointAccumulationRate > 0
            ? `${drivePassengerGradeEventData.eventPointAccumulationRate + drivePassengerGradeEventData.defaultAccumulationRate}%적립`
            : `${drivePassengerGradeEventData.defaultAccumulationRate}%적립`,
      }))
    }
  }, [drivePassengerGradeEventData])

  useEffect(() => {
    if (couponData) {
      setUserPoint((prev) => ({ ...prev, usableCouponCount: couponData.usableCouponCount }))
    }
  }, [couponData])

  useEffect(() => {
    if (bizCouponData) {
      setUserPoint((prev) => ({ ...prev, usableBizCouponCount: bizCouponData.data.availableCount }))
    }
  }, [bizCouponData])

  useEffect(() => {
    setUserProfileData((prev) => ({ ...prev, mdn: userInfoData?.mdn || '' }))
  }, [userInfoData])

  const getUserSetting = useCallback(async () => {
    const userSettings = (await TmapApp.getUserAllSettings()) as UserAllSettings

    if (!userSettings.settings.user.name) {
      TmapApp.notifyChangedInfo({ type: 'personalProfile' })
    }

    const vehicleType =
      vehicleModelTypes.find((model) => model.model === userSettings?.settings.car.model)?.name ||
      '승용차'

    setUserProfileData((prev) => ({
      ...prev,
      image: userSettings?.settings.user.image || '',
      name: userSettings?.settings.user.name || '',
    }))

    const nextVehicleInfo = {
      carNumber:
        vehicleType === '이륜차' && !userSettings?.settings.car.number
          ? '모터바이크'
          : userSettings?.settings.car.number || '',
      vehicleType,
      fuelType:
        vehicleType === '이륜차' ? '' : fuelTypes[userSettings?.settings.car.fuel] || '휘발유',
      isParking:
        (vehicleType !== '이륜차' &&
          parkingData?.some((v) => v.vehicleNumber === userSettings?.settings.car.number)) ||
        false,
    }
    setVehicleInfo(nextVehicleInfo)
  }, [parkingData])

  useEffect(() => {
    getLoginData()
    getUserSetting()
  }, [getUserSetting, getLoginData])

  const getRedDotList = useCallback(() => {
    TmapApp.getRedDotList().then((recvRedDotList) => {
      const redDotList = recvRedDotList.filter(
        (data) =>
          (data.itemGroup === 'SERVICE_INDEX' ||
            data.itemGroup === 'COMMUNICATION' ||
            data.itemGroup === 'HOME' ||
            data.item.includes('BOARD-')) &&
          data.displayType === 'REDDOT' &&
          tmapDateStringToDateTime(data.expiredDateTime) > dayjs() &&
          tmapDateStringToDateTime(data.startDateTime) <= dayjs() &&
          !data.readDateTime,
      )

      const messageRedDotList = recvRedDotList.filter(
        (data) =>
          data.itemGroup === 'SERVICE_INDEX' &&
          data.displayType === 'MESSAGE' &&
          tmapDateStringToDateTime(data.expiredDateTime) > dayjs() &&
          tmapDateStringToDateTime(data.startDateTime) <= dayjs() &&
          !data.readDateTime,
      )

      setServiceListRedDot(redDotList.map((d) => ({ itemGroup: d.itemGroup, item: d.item })))
      setPointRedDot(redDotList.filter((d) => d.item === 'POINT'))
      setInsuranceRedDot(redDotList.filter((d) => d.item === 'MY_INSURANCE'))
      setMissionRedDot(redDotList.filter((d) => d.item === 'MY_MISSION'))
      setBizCouponRedDot(redDotList.filter((d) => d.item === 'POI_COUPON'))
      setFollowingRedDot(redDotList.filter((d) => d.item === 'MY_FOLLOWING_CONTENTS'))
      setMessageList(messageRedDotList)
    })
  }, [])

  const refreshMyContent = useCallback(async () => {
    await Promise.all([
      getUserSetting(),
      getLoginData(),
      getRedDotList(),
      refetchPointData(),
      refetchServiceConfig(),
      refetchQuizData(),
      refetchMissionBannerList(),
      refetchDrivePassengerGradeEventData(),
      refetchCouponData(),
      refetchBizCouponData(),
      refetchUserInfoData(),
      refetchParkingData(),
      refetchReviewData(),
      refetchHomeQuiz(),
      refetchPoiVisitedCount(),
      refetchRewardShoppingPoint(),
    ])
    refetchFollowingContentsRef.current?.()
  }, [
    getUserSetting,
    getLoginData,
    getRedDotList,
    refetchPointData,
    refetchServiceConfig,
    refetchQuizData,
    refetchMissionBannerList,
    refetchDrivePassengerGradeEventData,
    refetchCouponData,
    refetchBizCouponData,
    refetchUserInfoData,
    refetchParkingData,
    refetchReviewData,
    refetchHomeQuiz,
    refetchPoiVisitedCount,
    refetchRewardShoppingPoint,
  ])

  useEffect(() => {
    onReadyRefreshMyContent?.(refreshMyContent)
  }, [onReadyRefreshMyContent, refreshMyContent])

  useEffect(() => {
    if (!serviceConfigData) return
    const configData = serviceConfigData.data.contents.reduce(
      (acc: any, item: any) => ({ ...acc, [item.key]: item.data }),
      {},
    )
    setGlobalStateValue((prev: any) => ({ ...prev, ...configData }))
  }, [serviceConfigData, setGlobalStateValue])

  useEffect(() => {
    setUserProfileData((prev) => ({ ...prev, isVerified }))
    if (isVerified !== null) getRedDotList()
  }, [isVerified, getRedDotList])

  useEffect(() => {
    if (
      isUserInfoDataFetched &&
      isBannerFetched &&
      isServiceConfigFetched &&
      isParkingDataFetched &&
      isPointDataFetched &&
      isQuizDataFetched &&
      isDrivePassengerGradeEventFetched &&
      isCouponDataFetched &&
      isBizCouponDataFetched &&
      isReviewDataFetched &&
      isHomeQuizFetched
    ) {
      setIsMainLoading(false)
    }
  }, [
    isUserInfoDataFetched,
    isBannerFetched,
    isServiceConfigFetched,
    isParkingDataFetched,
    isPointDataFetched,
    isQuizDataFetched,
    isDrivePassengerGradeEventFetched,
    isCouponDataFetched,
    isBizCouponDataFetched,
    isReviewDataFetched,
    isHomeQuizFetched,
  ])

  // android resume시 repaint
  useEffect(() => {
    if (TmapApp.env.isInApp) {
      const off = TmapApp.utils.addNativeEventListener('onResume', () => {
        const bodyEl = document.querySelector('body') as HTMLBodyElement
        bodyEl.style.transform = 'translate3d(0, 0, 0)'
        setTimeout(() => (bodyEl.style.transform = ''), 50)
      })
      return () => off()
    }
  }, [])

  useEffect(() => {
    return TmapApp.utils.addNativeEventListener('onResume', () => {
      refreshAutoplayOnResume()
    })
  }, [refreshAutoplayOnResume])

  useEffect(() => {
    return TmapApp.utils.addNativeEventListener('onResume', () => {
      // iOS content process 종료 후 reload → foreground 복귀 시나리오 방어 (TMAPCLIENT-30726).
      // 라이브러리 계약상 TmapApp.getAccessKey()는 300ms 안에 native callback을 못 받으면 '' resolve.
      // 재시도 없이 1회 호출하고 ''를 accessKeyRef에 덮어쓰면 다수 API가 빈 AK 헤더로 401 폭주 → logout 폭주.
      // → getAccessKeyWithRetry로 완충하고, 최종 결과가 빈 값이면 이전 accessKey를 유지한 채 refetch 자체를 건너뜀.
      getAccessKeyWithRetry({ retries: 3, interval: 200 }).then((newAk) => {
        if (!newAk) return
        if (accessKey !== newAk) {
          if (accessKeyRef) {
            accessKeyRef.current = newAk
          }
          setGlobalStateValue((prev) => ({ ...prev, accessKey: newAk }))
        }
        refreshMyContent().then(() => {
          setGlobalStateValue((prev) => ({ ...prev, isSkeleton: false }))
          setTimeout(() => setGlobalStateValue((prev) => ({ ...prev, isSkeleton: true })), 1500)
        })
      })
    })
  }, [accessKey, accessKeyRef, setGlobalStateValue, refreshMyContent])
  useEffect(() => {
    return TmapApp.utils.addNativeEventListener('onRefresh', () => {
      window.location.reload()
    })
  }, [])

  useEffect(() => {
    if (!isMainLoading) {
      tmapLogClient('expose').setPageId('/my').send()
    }
  }, [isMainLoading, tmapLogClient])

  useEffect(() => {
    const { extraObject = {}, pageIdInfo } = pageIdContext || {}
    const encodedLogBoxObject = encodeURIComponent(JSON.stringify({ logbox: extraObject.logbox }))

    if (TmapApp.env.appVersion !== '0.0.0') {
      if (TmapApp.env.isAndroid) {
        if (pageIdInfo) {
          console.log('pageIdInfo', pageIdInfo)
          const path = gte(TmapApp.env.appVersion, '11.00.00') ? `/web/my?extra=${encodedLogBoxObject}` : `/web?extra=${encodedLogBoxObject}`
          setGlobalStateValue((prev) => ({ ...prev, schemeURL: path }))
          setSchemeUrlStorageValue(path)
          window.history.back()
        }
        if (schemeUrlStorageValue) {
          window.location.replace(schemeUrlStorageValue)
          setTimeout(() => setGlobalStateValue((prev) => ({ ...prev, schemeURL: '' })), 0)
          setSchemeUrlStorageValue('')
        }
      } else {
        if (pageIdInfo) {
          const path = gte(TmapApp.env.appVersion, '11.00.00') ? `/web/my?extra=${encodedLogBoxObject}` : `/web?extra=${encodedLogBoxObject}`
          window.location.replace(path)
        }
      }
    }
  }, [pageIdContext, setGlobalStateValue, schemeUrlStorageValue, setSchemeUrlStorageValue])

  const callTmapBizInfoDebouncing = useMemo(
    () =>
      debounce(
        (actionLog: string) => {
          sendEventLog(actionLog)
          switch (env) {
          case 'dev':
            openServiceUrl(envServiceUrl.tmapBizInfo.dev, true)
            break
          case 'dtg':
          case 'stg':
            openServiceUrl(envServiceUrl.tmapBizInfo.stg, true)
            break
          case 'rtg':
          case 'prd':
            openServiceUrl(envServiceUrl.tmapBizInfo.prd, true)
            break
          }
        },
        1000,
        { leading: true, trailing: false },
      ),
    [env, openServiceUrl],
  )

  const handleClickTmapBizInfo = useCallback(
    (actionLog: string) => {
      callTmapBizInfoDebouncing(actionLog)
    },
    [callTmapBizInfoDebouncing],
  )

  const callClickDebouncing = useMemo(
    () =>
      debounce(
        (serviceUrl: string, actionLog: string) => {
          sendEventLog(actionLog)
          if (serviceUrl.startsWith('tmap://')) openService(serviceUrl)
          else {
            if (serviceUrl.startsWith('https://')) openServiceUrl(serviceUrl, true)
            else openServiceUrl(serviceUrl, true, true)
          }
        },
        50,
        { leading: true, trailing: false },
      ),
    [openService, openServiceUrl],
  )

  const handleClick = useCallback(
    (serviceUrl: string, actionLog: string) => {
      callClickDebouncing(serviceUrl, actionLog)
    },
    [callClickDebouncing],
  )

  // 에러 로그용 (중복 전송 방지) - 원본 로직 그대로 유지
  useEffect(() => {
    const hasAnyError =
      !!serviceConfigError ||
      !!pointDataError ||
      !!drivePassengerGradeEventError ||
      !!couponDataError ||
      !!bizCouponDatatError ||
      !!parkingDataError ||
      !!reviewDataError ||
      !!homeQuizError ||
      !!userInfoError

    if (hasAnyError && !isErrorLoggedRef.current) {
      if (serviceConfigError)
        logError('my:error:serviceConfigError', { error: serviceConfigError })
      if (pointDataError) logError('my:error:point', { error: pointDataError })
      if (drivePassengerGradeEventError) {
        logError('my:error:drivePassengerGradeEventError', { error: drivePassengerGradeEventError })
      }
      if (couponDataError) logError('my:error:coupon', { error: couponDataError })
      if (bizCouponDatatError)
        logError('my:error:bizCoupon', { error: bizCouponDatatError })
      if (parkingDataError)
        logError('my:error:parking', { error: parkingDataError })
      if (reviewDataError) logError('my:error:review', { error: reviewDataError })
      if (userInfoError) logError('my:error:userInfo', { error: userInfoError })
      if (homeQuizError)
        logError('my:error:homeQuiz', { error: homeQuizError })
      isErrorLoggedRef.current = true
    }

    if (!hasAnyError) isErrorLoggedRef.current = false
  }, [
    pointDataError,
    drivePassengerGradeEventError,
    couponDataError,
    bizCouponDatatError,
    parkingDataError,
    reviewDataError,
    homeQuizError,
    userInfoError,
    serviceConfigError,
  ])

  if (hasError) throw new Error('frontman initialization failed')

  if (showMainSkeleton && isMainLoading) {
    return <MainSkeleton />
  }

  return (
    <>
      {showInterfaceArea && <div className={styles.interfaceArea} />}

      {/* startFrom에 따라 UserProfile 포함 여부 결정 */}
      {startFrom === 'userProfile' && (
        <UserProfile
          loginAuthType={userProfileData?.loginAuthType || ''}
          image={userProfileData?.image || ''}
          name={userProfileData?.name || ''}
          mdn={userProfileData?.mdn || ''}
          tid={userProfileData?.tid || ''}
          isVerified={userProfileData?.isVerified || false}
        />
      )}

      <VehicleInfo
        carNumber={vehicleInfo?.carNumber ?? null}
        vehicleType={vehicleInfo?.vehicleType || ''}
        fuelType={vehicleInfo?.fuelType || ''}
        isParking={vehicleInfo?.isParking || false}
        redDotList={insuranceRedDot}
      />

      {isRewardShoppingModuleVisible && !isRewardShoppingPointError && (
        <RewardShopping
          rewardPoint={rewardShoppingPoint}
          benefitTagConfig={rewardShoppingTextConfig}
          onClick={() => {
            const nudgeText = rewardShoppingTextConfig?.isShow ? rewardShoppingTextConfig.text : null
            sendEventLog(action_id?.tapReward, {
              reward_amount: rewardShoppingPoint !== null && rewardShoppingPoint > 0 ? 'y' : 'n',
              nudge_text: nudgeText,
            })
            openService('tmap://vertical?service=mission&mission_type=reward-shopping')
          }}
        />
      )}

      <Point
        point={userPoint.point}
        mileage={userPoint.mileage}
        pointRedDotList={pointRedDot}
        grade={userPoint.grade}
        isEventOngoing={userPoint.isEventOngoing}
        text={userPoint.text}
        usableCouponCount={userPoint.usableCouponCount}
        usableBizCouponCount={userPoint.usableBizCouponCount}
        bizCouponRedDotList={bizCouponRedDot}
        poiVisitedCount={poiVisitedCount?.visitedCount}
      />

      <CavatarBanner data={cavatarBannerData} />

      <FollowingContents
        isRedDot={followingRedDot.length > 0}
        title={serviceConfigData?.data.contents.find((item) => item.key === 'MY_FOLLOWING_CONTENTS_TITLE')?.data?.title ?? '팔로잉 새소식'}
        position={followingPosition}
        canAutoplay={canAutoplay}
        resumeKey={resumeKey}
        onReadyRefetch={(refetch) => { refetchFollowingContentsRef.current = refetch }}
      />

      <PointMissionBanner
        isRedDot={missionRedDot.length > 0}
        missionBannerData={missionBannerList?.ads}
        isLoading={!isMissionBannerFetched}
        homeQuizData={homeQuizData}
        isFetched={isHomeQuizFetched}
        stepCounterData={undefined}
        showPedometer={false}
        isPedometerActive={null}
      />

      <PlaceReview data={reviewData || null} />

      <ServiceList
        redDotList={serviceListRedDot}
        messageList={messageList}
      />

      {bannerData && (
        <AdsBanner
          data={bannerData}
          isLoading={!isBannerFetched}
          using={'DEFAULT'}
        />
      )}

      <div className={styles.wrapper}>
        {bizPlaceBanner?.isShow && (
          <button
            data-testid="service_list_biz_banner"
            className={styles.bizPlaceBannerWrapper}
            onClick={() =>
              action_id?.tapBizPlaceBanner &&
              handleClick(bizPlaceBanner.url, action_id.tapBizPlaceBanner)
            }
          >
            <div>
              <p className={styles.bizPlaceTitle}>비즈 플레이스</p>
              <p>티맵에 업체 등록하고 관리하기</p>
            </div>
            <Image
              src={BizBanner}
              alt={''}
            />
          </button>
        )}

        <button
          data-testid="tmap_biz_info"
          className={styles.tmapBizInfo}
          onClick={() =>
            action_id?.tapBusinessInfo && handleClickTmapBizInfo(action_id.tapBusinessInfo)
          }
        >
          티맵모빌리티(주) 사업자정보
        </button>
      </div>
    </>
  )
}
